// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, EMPTY, forkJoin, Observable, of } from "rxjs"
import { flatten } from "ramda"
import { Action } from "redux"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { t } from "@lingui/macro"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as TagsActions from "../actions"
import * as TagsSlice from "../slice"
import { showSnackBar } from "../../snackbar/actions"
import { getTagCategoryIdsList } from "./getTagCategoryIdsList"
import {
  BatchGetTagCategoriesResponse,
  BatchGetTagCategoriesRequestSchema,
  BatchListTagsByCategoriesResponse,
  BatchListTagsByCategoriesRequestSchema,
} from "../../../generated/legebill/site/tag/v4/tag_pb"
import { executor, tagService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"

type BatchGetTagsByCategoriesRpc = (tagCategoryIdsList: string[]) => Observable<BatchGetTagCategoriesResponse>
type BatchListTagsByCategoriesRpc = (tagCategoryIdsList: string[]) => Observable<BatchListTagsByCategoriesResponse>

export const batchGetTagCategories = (
  batchGetTagCategoriesRpc: BatchGetTagsByCategoriesRpc,
  batchListTagsByCategoriesRpc: BatchListTagsByCategoriesRpc,
) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(action => (
        TagsActions.batchGetTagCategories.match(action)
        || TimelineSlice.setTimeline.match(action)
        || TimelineSlice.setTimeRowsForDates.match(action)
      )),
      mergeMap((action) => {
        const tagCategoryIdsList = getTagCategoryIdsList(action)
        if (tagCategoryIdsList.length === 0) {
          return EMPTY
        }
        return forkJoin([
          batchGetTagCategoriesRpc(tagCategoryIdsList),
          batchListTagsByCategoriesRpc(tagCategoryIdsList),
        ]).pipe(
          mergeMap(([batchGetTagCategoriesResponse, batchListTagsByCategoriesResponse]) => concat(
            of(TagsSlice.setTagCategories(batchGetTagCategoriesResponse.tagCategories)),
            of(TagsSlice.setTags(flatten(
              batchListTagsByCategoriesResponse.tagsByCategories.map(t => t.tags)
            ))),
          )),
          catchError(_ => of(showSnackBar({
            message: t({
              id: "msg_batch_get_tag_categories_failed",
              message: "Failed to fetch tag category details. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
            }),
            messageType: "ERROR",
          }))),
        )
      }),
    )

const batchGetTagCategoriesRpc = (tagCategoryIdsList: string[]): Observable<BatchGetTagCategoriesResponse> =>
  executor(() => tagService.batchGetTagCategories(
    create(BatchGetTagCategoriesRequestSchema, { tagCategoryIds: tagCategoryIdsList })
  ))

const batchListTagsByCategoriesRpc = (tagCategoryIdsList: string[]): Observable<BatchListTagsByCategoriesResponse> =>
  executor(() => tagService.batchListTagsByCategories(
    create(BatchListTagsByCategoriesRequestSchema, { tagCategoryIds: tagCategoryIdsList })
  ))

export default batchGetTagCategories(batchGetTagCategoriesRpc, batchListTagsByCategoriesRpc)
